/**
 * The door to the new design's home page (prest0.ai/prest0design/).
 *
 * App.tsx renders this for the route `/` ONLY inside the preview tree. It is on
 * the first download, so it holds nothing but the one dynamic import of the
 * `pavilion` chunk — the `pavilion` row of scripts/check-chat-first-paint.mjs
 * keeps the page itself (and the materials twin it reads) off the first
 * download and off the chat engine's file, and budgets it in
 * config/bundle-budgets.json.
 *
 * WHY useDeferredModule AND NOT React.lazy: App.tsx records React.lazy stalling
 * on the FIRST mount of `/` in production (2026-05-09), and this door mounts on
 * exactly that first render. The hook is a plain import() resolved into state —
 * no Suspense, nothing thrown during render — and it retries a failed download
 * three times before handing a chunk that is gone for good to the stale-tab
 * self-heal (src/utils/useDeferredModule.ts).
 *
 * While the chunk arrives the door paints the pavilion's sky in the theme the
 * page will open in, so the arrival is a calm fade rather than a flash of the
 * wrong ground. If the chunk cannot be had at all, the visitor gets today's home
 * page — the same app — never a blank one.
 */
import React from 'react';
import { useDeferredModule } from '../../utils/useDeferredModule';
import { semanticDark, semanticLight } from '../../styles/semanticTokens.generated';
import { resolvePreviewTheme } from '../previewTheme';
import { preloadPavilionFont } from './pavilionFont';

export interface PavilionHomeDoorProps {
  /** What renders if the new design's chunk cannot be loaded: today's home page. */
  fallback: React.ReactNode;
}

const PavilionHomeDoor: React.FC<PavilionHomeDoorProps> = ({ fallback }) => {
  // The face travels while the chunk does, so the headline paints in it rather than swapping to it.
  React.useEffect(() => preloadPavilionFont(), []);
  const { module, error } = useDeferredModule(
    () => import(/* webpackChunkName: "pavilion" */ './PavilionHome'),
    'pavilion-home',
    { start: 'mount' },
  );

  if (module) {
    const PavilionHome = module.default;
    return <PavilionHome />;
  }
  if (error) return <>{fallback}</>;

  const dark = resolvePreviewTheme() === 'dark';
  return (
    <div
      data-pavilion-door=""
      aria-busy="true"
      style={{ minHeight: '100vh', background: dark ? semanticDark.canvas : semanticLight.canvas }}
    />
  );
};

export default PavilionHomeDoor;
